<script setup lang="ts">
import VideoPlayer from "~/components/VideoPlayer.vue";
import LiveChat from "~/components/Chat/Index.vue";
import type { Collection } from "~/types";
import type { PropType } from "vue";
import { repository } from "~/repository";
import CountdownTimer from "~/components/Video/CountdownTimer.vue";
import useFavorites from "~/composables/useFavorites";
import useNotifications from "~/composables/useNotifications";
import moment from "moment";
import { useChatStore } from "~/stores/chat/store";
import { useWindowSize } from "@vueuse/core";
import { getThumbnailImage } from "~/utils/common";
import PlayerActionButtons from "~/components/Video/PlayerActionButtons.vue";

const props = defineProps({
  video: {
    type: Object as PropType<Collection & { onAir: boolean }>,
    required: true,
  },
});

const {
  favoriteVisible,
  favoriteTooltip,
  favoriteIcon,
  toggleFavorites,
  isFavoriteLoading,
} = useFavorites(props.video);

const {
  notificationsTooltip,
  notificationsIcon,
  toggleNotifications,
  isNotificationsLoading,
  notificationsVisible,
} = useNotifications(props.video);

const { width } = useWindowSize();

const currentVideoProgress = ref<number>(0);
const deviceType = ref<string>();

const chatStore = useChatStore();
const { chatEnabled, showChat } = storeToRefs(chatStore);

const videoSource = computed(() => {
  if (props.video?.type === "live") {
    return [{ file: props.video.source }];
  } else {
    return "https://cdn.jwplayer.com/v2/media/" + props.video.source;
  }
});

async function constantCheckStatus() {
  await chatStore.getStatusData(props.video.id);
  setTimeout(() => constantCheckStatus(), 10_000);
}

onMounted(async () => {
  deviceType.value = getDeviceType();
  if (props.video?.type === "live") {
    await constantCheckStatus();
  }

  const { data } = await repository.videos.getProgress();
  if (data.videos.length) {
    data.videos.forEach((el) => {
      if (el.id === props.video?.id)
        currentVideoProgress.value = el.progress || 0;
    });
  }
});

const isSmallWidth = computed(() => width.value > 768);

const showTrailer = ref(false);

const trailerPlayerRef = ref();
const videoPlayerRef = ref();

function toggleTrailer() {
  trailerPlayerRef.value?.cleanupPlayer();
  videoPlayerRef.value?.cleanupPlayer();
  showTrailer.value = !showTrailer.value;
}

function openStoreTDownload() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS) {
    window.open(
      "https://apps.apple.com/us/app/pokergo-stream-poker-tv/id1235783484",
      "_blank",
    );
  } else {
    window.open(
      "https://play.google.com/store/apps/details?id=com.pokercentral.poker",
      "_blank",
    );
  }
}

const playerActionButtonsRef = ref();

function onPlayerEnded() {
  playerActionButtonsRef?.value?.autoPlayNext();
}
</script>

<template>
  <div class="flex gap-4">
    <div class="flex-grow">
      <div class="flex justify-center">
        <div class="flex-grow">
          <template v-if="video.onAir && !showTrailer">
            <VideoPlayer
              :id="video.id"
              ref="videoPlayerRef"
              :source="videoSource"
              :duration="video.duration"
              :video-type="video.type"
              :progress="currentVideoProgress || 0"
              :track-progress="true"
              @on-player-ended="onPlayerEnded"
            />
            <div>
              <PlayerActionButtons
                ref="playerActionButtonsRef"
                :video="video"
              />
            </div>
          </template>
          <template v-else-if="video?.trailer_source && showTrailer">
            <VideoPlayer
              :id="video.id"
              ref="trailerPlayerRef"
              :source="`https://cdn.jwplayer.com/v2/media/${video.trailer_source}`"
              :duration="video.duration"
              video-type="vod"
              :progress="0"
              :track-progress="false"
            />
          </template>
          <div v-else class="relative mx-auto max-w-screen-lg">
            <NuxtImg
              alt="thumbnail"
              class="w-full"
              :src="getThumbnailImage([video.pre_air_images, video.images])"
              placeholder="/images/placeholder.png"
              loading="lazy"
            />

            <CountdownTimer
              :air-date="new Date(video.aired_at)"
              :video-type="video.type"
            />
          </div>
        </div>
      </div>
      <div
        v-if="!isSmallWidth && video.type === 'live' && chatEnabled && showChat"
        class="mt-10"
      >
        <LiveChat :video-id="video.id" />
      </div>
      <div class="mx-auto mt-10 flex flex-col gap-5 md:flex-row lg:mx-0">
        <div class="flex-grow">
          <div
            v-if="deviceType !== 'desktop'"
            :class="[
              'mb-10 flex items-center justify-center gap-5 rounded-lg bg-primary-500 p-2',
              { hidden: !chatEnabled },
            ]"
            @click="openStoreTDownload"
          >
            <div class="flex-shrink-0">
              <img
                src="~/assets/images/logos/pokergo-square-logo.png"
                alt="pokergo-square-logo"
              />
            </div>
            <div>To Enable Chat you would need transfer to PokerGO App</div>
          </div>
          <div class="rounded-lg bg-surface-bg p-5 py-8">
            <div class="flex items-center justify-between gap-3">
              <h1 class="text-2xl font-semibold">
                {{ video.title }}
              </h1>

              <div class="flex items-center gap-5">
                <PgIcon
                  v-if="notificationsVisible"
                  v-tooltip="notificationsTooltip"
                  :icon="notificationsIcon"
                  :loading="isNotificationsLoading"
                  @click="toggleNotifications"
                />
                <PgIcon
                  v-if="favoriteVisible"
                  v-tooltip="favoriteTooltip"
                  :icon="favoriteIcon"
                  :loading="isFavoriteLoading"
                  @click="toggleFavorites"
                />
              </div>
            </div>

            <div v-if="video?.subheading" class="mt-1 text-primary-200">
              {{ video.subheading }}
            </div>

            <div class="mt-1 text-surface-300">
              Original Airdate
              {{ moment(video.aired_at).format("MMM D, YYYY h:mm A") }}
            </div>
            <div v-if="video.commentary_by" class="mt-1 text-surface-300">
              Commentary by:
              {{ video.commentary_by }}
            </div>

            <div class="mt-3 text-lg">
              {{ video.description }}
            </div>
          </div>
        </div>

        <div v-if="!showChat" class="flex-shrink-0 md:w-96">
          <Button
            v-if="video.trailer_source"
            outlined
            class="mb-5"
            fluid
            @click="toggleTrailer"
          >
            <span v-if="!showTrailer">
              <font-awesome-icon :icon="['fas', 'play']" class="mr-2" />
              Watch Trailer
            </span>
            <span v-else>
              <font-awesome-icon :icon="['fas', 'stop']" class="mr-2" />
              Hide Trailer
            </span>
          </Button>
          <Button
            v-if="chatEnabled && deviceType === 'desktop'"
            outlined
            label="Enable Chat"
            class="mb-5"
            fluid
            @click="showChat = true"
          />
          <div
            v-if="video?.recommendations && video.recommendations.length > 0"
          >
            <div class="mb-2 font-bold">You Might Also Like</div>
            <div class="flex flex-col gap-2">
              <HorizChipCard
                v-for="(el, idx) in video.recommendations"
                :key="`rec_${idx}`"
                :data="el"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isSmallWidth && video.type === 'live' && chatEnabled && showChat"
    >
      <LiveChat :video-id="video.id" />
    </div>
  </div>
</template>

<style scoped></style>
